import React from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  FormDataConsumer,
  useNotify,
  useRefresh,
  useRecordContext,
  Button,
} from "react-admin";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmailIcon from "@mui/icons-material/Email";
import PeopleIcon from "@mui/icons-material/People";

const targetChoices = [
  { id: "all", name: "Echte Newsletter-Abonnenten" },
  { id: "selected", name: "Manuelle Newsletter-Empfänger" },
  { id: "active_basis_users", name: "Free-User" },
  { id: "active_trial14_users", name: "Trial14-User mit aktivem Trial" },
  { id: "expired_trial_basis_users", name: "Ehemalige Trial14-User, jetzt Free" },
  { id: "selected_users", name: "Ausgewählte User" },
  { id: "paid_users", name: "Pay-Nutzer mit aktivem Abo" },
];

const csvFromList = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return value ?? "";
};

const listFromCsv = (value: unknown) => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return value ?? [];
};

const userIdListFromCsv = (value: unknown) => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => (item.match(/^\d+$/) ? Number(item) : item))
      .filter((item) => typeof item === "number");
  }
  return value ?? [];
};

const getErrorMessage = async (response: Response, fallback: string) => {
  try {
    const data = await response.json();
    return data.detail || fallback;
  } catch (error) {
    return fallback;
  }
};

type PreviewRecipient = string | {
  email?: string;
  first_name?: string;
  source?: string;
};

type PreviewExcludedRecipient = {
  email?: string;
  reason?: string;
  first_name?: string;
  source?: string;
};

type RecipientPreview = {
  count: number;
  warning?: string;
  recipients?: PreviewRecipient[];
  excluded?: PreviewExcludedRecipient[];
};

const recipientLabel = (recipient: PreviewRecipient) => {
  if (typeof recipient === "string") {
    return recipient;
  }
  return [recipient.email, recipient.first_name, recipient.source].filter(Boolean).join(" - ");
};

const excludedLabel = (recipient: PreviewExcludedRecipient) => {
  return [recipient.email, recipient.reason, recipient.first_name, recipient.source].filter(Boolean).join(" - ");
};

const matchesPreviewFilter = (value: PreviewRecipient | PreviewExcludedRecipient, filter: string) => {
  if (!filter) {
    return true;
  }
  const text = typeof value === "string" ? value : Object.values(value).filter(Boolean).join(" ");
  return text.toLowerCase().includes(filter.toLowerCase());
};

const SendCampaignButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const handleSend = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const previewResponse = await fetch(`/api/newsletter/campaigns/${record.id}/recipient_preview`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!previewResponse.ok) {
        notify(await getErrorMessage(previewResponse, "Empfaengerpruefung fehlgeschlagen"), { type: "error" });
        return;
      }
      const preview = await previewResponse.json();
      const count = Number(preview.count || 0);
      if (count <= 0) {
        notify(preview.warning || "Keine gueltigen Empfaenger gefunden", { type: "warning" });
        return;
      }

      const confirmed = window.confirm(
        `Diese Kampagne wird an ${count} gueltige Empfaenger gesendet. Jetzt final versenden?`
      );
      if (!confirmed) {
        return;
      }

      const response = await fetch(`/api/newsletter/campaigns/${record.id}/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          confirm: true,
          expected_recipient_count: count,
        }),
      });
      if (!response.ok) {
        notify(await getErrorMessage(response, "Versand fehlgeschlagen"), { type: "error" });
        return;
      }
      notify("Kampagne wurde in die Queue gestellt", { type: "info" });
      refresh();
    } catch (error) {
      notify("Versand fehlgeschlagen", { type: "error" });
    }
  };

  return (
    <Button
      label="Send Campaign"
      onClick={handleSend}
      startIcon={<SendIcon />}
      disabled={record.status !== "draft"}
    />
  );
};

const RecipientPreviewPanel = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [preview, setPreview] = React.useState<RecipientPreview | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState("");

  if (!record) return null;

  const handlePreviewRecipients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`/api/newsletter/campaigns/${record.id}/recipient_preview`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        notify(await getErrorMessage(response, "Empfaengerpruefung fehlgeschlagen"), { type: "error" });
        return;
      }
      const data = await response.json();
      setPreview({
        count: Number(data.count || 0),
        warning: data.warning || "",
        recipients: Array.isArray(data.recipients) ? data.recipients : [],
        excluded: Array.isArray(data.excluded) ? data.excluded : [],
      });
    } catch (error) {
      notify("Empfaengerpruefung fehlgeschlagen", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const recipients = (preview?.recipients || []).filter((recipient) => matchesPreviewFilter(recipient, filter));
  const excluded = (preview?.excluded || []).filter((recipient) => matchesPreviewFilter(recipient, filter));

  return (
    <div style={{ border: "1px solid #ddd", borderRadius: 6, padding: 16, marginTop: 12 }}>
      <Button
        label={loading ? "Empfaenger werden geprueft..." : "Empfaenger pruefen"}
        onClick={handlePreviewRecipients}
        startIcon={<PeopleIcon />}
        disabled={loading}
      />

      {preview ? (
        <div style={{ marginTop: 12 }}>
          <strong>Gueltige Empfaenger: {preview.count}</strong>
          {preview.warning ? (
            <div style={{ marginTop: 8, color: "#8a5a00" }}>{preview.warning}</div>
          ) : null}

          <input
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Preview filtern nach E-Mail oder Grund"
            style={{
              boxSizing: "border-box",
              width: "100%",
              marginTop: 12,
              padding: "8px 10px",
              border: "1px solid #ccc",
              borderRadius: 4,
            }}
          />

          <div style={{ marginTop: 12 }}>
            <strong>Gueltige Beispiele</strong>
            {recipients.length ? (
              <ul style={{ marginTop: 6 }}>
                {recipients.map((recipient, index) => (
                  <li key={`${recipientLabel(recipient)}-${index}`}>{recipientLabel(recipient)}</li>
                ))}
              </ul>
            ) : (
              <div style={{ marginTop: 6 }}>Keine Eintraege in der Preview.</div>
            )}
          </div>

          <div style={{ marginTop: 12 }}>
            <strong>Ausgeschlossen</strong>
            {excluded.length ? (
              <ul style={{ marginTop: 6 }}>
                {excluded.map((recipient, index) => (
                  <li key={`${excludedLabel(recipient)}-${index}`}>{excludedLabel(recipient)}</li>
                ))}
              </ul>
            ) : (
              <div style={{ marginTop: 6 }}>Keine ausgeschlossenen Eintraege in der Preview.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const TestSendCampaignButton = () => {
  const record = useRecordContext();
  const notify = useNotify();

  if (!record) return null;

  const handleTestSend = async () => {
    const email = window.prompt("Testmail senden an:");
    if (!email) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`/api/newsletter/campaigns/${record.id}/test_send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        notify(await getErrorMessage(response, "Testmail fehlgeschlagen"), { type: "error" });
        return;
      }
      notify("Testmail wurde gesendet", { type: "info" });
    } catch (error) {
      notify("Testmail fehlgeschlagen", { type: "error" });
    }
  };

  return <Button label="Testmail senden" onClick={handleTestSend} startIcon={<EmailIcon />} />;
};

const PreviewCampaignButton = () => {
  const record = useRecordContext();
  const notify = useNotify();

  if (!record) return null;

  const handlePreview = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`/api/newsletter/campaigns/${record.id}/preview`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        notify(await getErrorMessage(response, "Preview fehlgeschlagen"), { type: "error" });
        return;
      }
      const html = await response.text();
      const previewWindow = window.open("", "_blank");
      if (!previewWindow) {
        notify("Popup blockiert", { type: "warning" });
        return;
      }
      previewWindow.document.open();
      previewWindow.document.write(html);
      previewWindow.document.close();
    } catch (error) {
      notify("Preview fehlgeschlagen", { type: "error" });
    }
  };

  return (
    <Button
      label="Preview Mail"
      onClick={handlePreview}
      startIcon={<VisibilityIcon />}
    />
  );
};

export const NewsletterCampaignList = () => (
  <List>
    <Datagrid rowClick="edit">
      <TextField source="name" />
      <TextField source="subject" />
      <TextField source="target_type" />
      <TextField source="status" />
      <DateField source="sent_at" showTime />
    </Datagrid>
  </List>
);

const CampaignFormFields = () => (
  <>
    <TextInput source="name" fullWidth />
    <TextInput source="subject" fullWidth />
    <TextInput source="from_email" fullWidth />
    <SelectInput source="target_type" choices={targetChoices} fullWidth defaultValue="all" />
    <FormDataConsumer>
      {({ formData }) => {
        const showEmailRecipients = formData.target_type === "selected";
        const showUserIds = formData.target_type === "selected" || formData.target_type === "selected_users";
        const showTrialEndFilters = formData.target_type === "expired_trial_basis_users";

        return showEmailRecipients || showUserIds || showTrialEndFilters ? (
          <>
            {showEmailRecipients ? (
              <TextInput
                source="recipient_emails"
                fullWidth
                multiline
                minRows={2}
                label="Empfaenger E-Mails (kommagetrennt)"
                format={csvFromList}
                parse={listFromCsv}
              />
            ) : null}
            {showUserIds ? (
              <TextInput
                source="recipient_user_ids"
                fullWidth
                multiline
                minRows={2}
                label="Empfaenger User-IDs (kommagetrennt)"
                format={csvFromList}
                parse={userIdListFromCsv}
              />
            ) : null}
            {showTrialEndFilters ? (
              <>
                <TextInput
                  source="trial_ended_from"
                  type="date"
                  fullWidth
                  label="Trial beendet von"
                  helperText="Leer lassen, um keine Grenze zu setzen. Filtert ehemalige Trial14-Nutzer nach dem Ende ihrer Testphase. Free bedeutet: keine aktive Trial- oder Pay-Subscription; Luma Basis ist ein bezahltes Abo."
                />
                <TextInput
                  source="trial_ended_to"
                  type="date"
                  fullWidth
                  label="Trial beendet bis"
                  helperText="Leer lassen, um keine Grenze zu setzen. Filtert ehemalige Trial14-Nutzer nach dem Ende ihrer Testphase. Free bedeutet: keine aktive Trial- oder Pay-Subscription; Luma Basis ist ein bezahltes Abo."
                />
              </>
            ) : null}
          </>
        ) : null;
      }}
    </FormDataConsumer>
    <TextInput
      source="body_html"
      fullWidth
      multiline
      minRows={6}
      helperText="Verfügbare Variablen: {{ greeting }}, {{ full_name }}, {{ first_name }}, {{ email }}, {{ unsubscribe_url }}, {{ unsubscribe_newsletter_url }}, {{ unsubscribe_product_url }}, {{ unsubscribe_all_url }}. {{ unsubscribe_url }} passt automatisch zur Zielgruppe; spezifische Links koennen fuer eigene Footer genutzt werden."
    />
    <TextInput
      source="body_text"
      fullWidth
      multiline
      minRows={4}
      helperText="Verfügbare Variablen: {{ greeting }}, {{ full_name }}, {{ first_name }}, {{ email }}, {{ unsubscribe_url }}, {{ unsubscribe_newsletter_url }}, {{ unsubscribe_product_url }}, {{ unsubscribe_all_url }}. {{ unsubscribe_url }} passt automatisch zur Zielgruppe; spezifische Links koennen fuer eigene Footer genutzt werden."
    />
    <TextInput source="status" disabled />
    <TextInput source="sent_at" disabled />
  </>
);

export const NewsletterCampaignCreate = () => (
  <Create>
    <SimpleForm sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
      <CampaignFormFields />
    </SimpleForm>
  </Create>
);

export const NewsletterCampaignEdit = () => (
  <Edit>
    <SimpleForm sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
      <CampaignFormFields />
      <PreviewCampaignButton />
      <RecipientPreviewPanel />
      <TestSendCampaignButton />
      <SendCampaignButton />
    </SimpleForm>
  </Edit>
);
